import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import DeleteIcon from '@atlaskit/icon/core/delete';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { type JSX, type ReactNode, useState } from 'react';

export type SuggestionCardProps = {
	/** Product/feature icon for the card header */
	icon: ReactNode;
	/** Card title */
	title: ReactNode;
	/** Card description, can include inline smart links */
	description: ReactNode;
	/** CTA button label */
	ctaLabel: ReactNode;
	/** CTA button icon (optional) */
	ctaIcon?: ReactNode;
	/** CTA button click handler */
	onCtaClick: () => void;
	/** Snooze button click handler */
	onSnoozeClick?: () => void;
};

const cardStyles = xcss({
	borderRadius: 'radius.large',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface',
	padding: 'space.200',
	flexGrow: '1',
	flexBasis: '0',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	boxSizing: 'border-box',
});

const stackStyles = xcss({
	flexGrow: '1',
	display: 'flex',
	flexDirection: 'column',
});

const descriptionStyles = xcss({
	flexGrow: '1',
});

const headerStyles = xcss({
	paddingInlineEnd: 'space.400',
});

const ctaContainerStyles = xcss({
	paddingTop: 'space.100',
	marginTop: 'auto',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

export const SuggestionCard = ({
	icon,
	title,
	description,
	ctaLabel,
	ctaIcon,
	onCtaClick,
	onSnoozeClick,
}: SuggestionCardProps): JSX.Element => {
	const [isHoveredOrFocused, setIsHoveredOrFocused] = useState(false);

	return (
		<Box
			xcss={cardStyles}
			onMouseEnter={() => setIsHoveredOrFocused(true)}
			onMouseLeave={() => setIsHoveredOrFocused(false)}
			onFocusCapture={() => setIsHoveredOrFocused(true)}
			onBlurCapture={() => setIsHoveredOrFocused(false)}
		>
			<Stack space="space.150" xcss={stackStyles}>
				{/* Header: icon + title */}
				<Inline alignBlock="center" space="space.100" xcss={headerStyles}>
					{icon}
					<Text weight="semibold" color="color.text">
						{title}
					</Text>
				</Inline>

				{/* Description */}
				<Box xcss={descriptionStyles}>
					<Text color="color.text.subtlest" size="medium">
						{description}
					</Text>
				</Box>

				{/* CTA */}
				<Box xcss={ctaContainerStyles}>
					<Inline alignInline="end">
						<Button appearance="subtle" onClick={onCtaClick}>
							<Inline alignBlock="center" space="space.050">
								{ctaIcon}
								<span
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										display: 'block',
									}}
								>
									<Text color="color.text.subtle" size="medium">
										{ctaLabel}
									</Text>
								</span>
							</Inline>
						</Button>
					</Inline>
				</Box>
			</Stack>

			{/* Snooze button — always in DOM for keyboard accessibility.
			    Visible on hover or when any element within the card has focus. */}
			{onSnoozeClick && (
				<div
					style={{
						position: 'absolute',
						top: '8px',
						right: '8px',
						opacity: isHoveredOrFocused ? 1 : 0,
						transition: 'opacity 0.15s ease',
					}}
				>
					<IconButton
						icon={DeleteIcon}
						label="Snooze suggestion"
						appearance="subtle"
						spacing="compact"
						onClick={onSnoozeClick}
					/>
				</div>
			)}
		</Box>
	);
};
